import React, { createContext, useContext, useState, useRef, useEffect } from 'react';
import { useAuth } from '@/providers/AuthProvider';
import { useToast } from '@/components/ui/use-toast';
import { db } from '@/lib/firebase';
import { callSounds } from '@/lib/callSounds';
import { trackTelemetryEvent } from '@/lib/observability/telemetry';
import { 
  collection, 
  doc, 
  setDoc, 
  updateDoc, 
  onSnapshot, 
  deleteDoc,
  addDoc,
  arrayUnion,
  serverTimestamp,
  query,
  where,
  getDocs,
  getDoc,
  Timestamp
} from '@firebase/firestore';
import { addNotification } from '@/services/notificationService';

type CallAnalyticsEventName =
  | 'call.adaptive.triggered'
  | 'call.adaptive.recovered'
  | 'call.reconnect.attempt'
  | 'call.reconnect.recovered'
  | 'call.reconnect.failed'
  | 'call.reconnect.summary';

interface CallData {
  id: string;
  caller: {
    id: string;
    name: string;
    avatar: string;
  };
  receiver: {
    id: string;
    name: string;
    avatar: string;
  };
  type: 'audio' | 'video';
  status: 'ringing' | 'connected' | 'ended' | 'missed' | 'rejected';
  startTime?: Date;
  endTime?: Date;
  duration?: number;
}

interface CallContextType {
  currentCall: CallData | null;
  incomingCall: CallData | null;
  isInCall: boolean;
  isMuted: boolean;
  isVideoOn: boolean;
  remoteAudioMuted: boolean;
  isScreenSharing: boolean;
  isAdaptiveQualityEnabled: boolean;
  adaptiveVideoMode: 'normal' | 'auto-audio-only';
  qualityProfile: 'hd' | 'balanced' | 'data-saver';
  isCallMinimized: boolean;
  canScreenShare: boolean;
  localStream: MediaStream | null;
  remoteStream: MediaStream | null;
  screenStream: MediaStream | null;
  callQuality: {
    status: 'excellent' | 'good' | 'fair' | 'poor';
    score: number;
    rttMs: number;
    jitterMs: number;
    packetLossPercent: number;
    bitrateKbps: number;
    recommendations: string[];
    sampledAt: number;
  } | null;
  
  // Actions
  startCall: (receiverId: string, receiverName: string, receiverAvatar: string, type: 'audio' | 'video') => Promise<void>;
  startScreenShareCall: (receiverId: string, receiverName: string, receiverAvatar: string) => Promise<void>;
  endCall: () => Promise<void>;
  acceptCall: (callId: string) => Promise<void>;
  rejectCall: (callId: string) => Promise<void>;
  toggleMute: () => void;
  toggleVideo: () => void;
  toggleRemoteAudio: () => void;
  toggleScreenShare: () => Promise<void>;
  toggleAdaptiveQuality: () => void;
  setQualityProfile: (profile: 'hd' | 'balanced' | 'data-saver') => Promise<void>;
  minimizeCall: () => void;
  maximizeCall: () => void;
}

const CallContext = createContext<CallContextType | undefined>(undefined);

export function useCall() {
  const context = useContext(CallContext);
  if (!context) {
    throw new Error('useCall must be used within a CallProvider');
  }
  return context;
}

interface CallProviderProps {
  children: React.ReactNode;
  enabled?: boolean;
}

export function CallProvider({ children, enabled = true }: CallProviderProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  
  const [currentCall, setCurrentCall] = useState<CallData | null>(null);
  const [incomingCall, setIncomingCall] = useState<CallData | null>(null);
  const [isInCall, setIsInCall] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOn, setIsVideoOn] = useState(true);
  const [remoteAudioMuted, setRemoteAudioMuted] = useState(false);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [isAdaptiveQualityEnabled, setIsAdaptiveQualityEnabled] = useState(true);
  const [adaptiveVideoMode, setAdaptiveVideoMode] = useState<'normal' | 'auto-audio-only'>('normal');
  const [qualityProfile, setQualityProfileState] = useState<'hd' | 'balanced' | 'data-saver'>('balanced');
  const [isCallMinimized, setIsCallMinimized] = useState(false);
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null);
  const [screenStream, setScreenStream] = useState<MediaStream | null>(null);
  const [callQuality, setCallQuality] = useState<{
    status: 'excellent' | 'good' | 'fair' | 'poor';
    score: number;
    rttMs: number;
    jitterMs: number;
    packetLossPercent: number;
    bitrateKbps: number;
    recommendations: string[];
    sampledAt: number;
  } | null>(null);
  
  const peerConnection = useRef<RTCPeerConnection | null>(null);
  const localVideoRef = useRef<HTMLVideoElement>(null);
  const remoteVideoRef = useRef<HTMLVideoElement>(null);
  const callUnsubscribe = useRef<(() => void) | null>(null);
  const incomingCallsUnsubscribe = useRef<(() => void) | null>(null);
  const processedCandidates = useRef<Set<string>>(new Set());
  const callTimeoutId = useRef<number | null>(null);
  const disconnectTimeoutId = useRef<number | null>(null);
  const incomingAutoMissTimeouts = useRef<Map<string, number>>(new Map());
  const isInCallRef = useRef<boolean>(false);
  const currentCallRef = useRef<CallData | null>(null);
  const incomingCallRef = useRef<CallData | null>(null);
  const isScreenSharingRef = useRef<boolean>(false);
  const isAdaptiveQualityEnabledRef = useRef<boolean>(true);
  const isVideoOnRef = useRef<boolean>(true);
  const adaptiveVideoModeRef = useRef<'normal' | 'auto-audio-only'>('normal');
  const qualityProfileRef = useRef<'hd' | 'balanced' | 'data-saver'>('balanced');
  const localStreamRef = useRef<MediaStream | null>(null);
  const remoteStreamRef = useRef<MediaStream | null>(null);
  const screenStreamRef = useRef<MediaStream | null>(null);
  const cameraTrackBeforeShareRef = useRef<MediaStreamTrack | null>(null);
  const pendingStartScreenShareRef = useRef<boolean>(false);
  const qualityBaselineRef = useRef<{ bytes: number; at: number }>({ bytes: 0, at: 0 });
  const qualityTrendRef = useRef<{ poorStreak: number; goodStreak: number; lastAutoSwitchAt: number }>({
    poorStreak: 0,
    goodStreak: 0,
    lastAutoSwitchAt: 0,
  });
  const reconnectAttemptCountRef = useRef(0);
  const reconnectAnalyticsRef = useRef<{
    attempts: number;
    successfulRecoveries: number;
    failedRecoveries: number;
  }>({
    attempts: 0,
    successfulRecoveries: 0,
    failedRecoveries: 0,
  });
  const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  const canScreenShare = typeof navigator !== 'undefined'
    && !!navigator.mediaDevices
    && typeof navigator.mediaDevices.getDisplayMedia === 'function';

  // WebRTC configuration with STUN servers
  const rtcConfig = {
    iceServers: [
      { urls: 'stun:stun.l.google.com:19302' },
      { urls: 'stun:stun1.l.google.com:19302' },
      { urls: 'stun:stun2.l.google.com:19302' },
      { urls: 'stun:stun3.l.google.com:19302' },
      { urls: 'stun:stun4.l.google.com:19302' }
    ],
    iceCandidatePoolSize: 10
  };

  const qualityProfileConfig: Record<'hd' | 'balanced' | 'data-saver', {
    label: string;
    videoMaxBitrate: number;
    videoMaxFramerate: number;
    videoScaleDownBy: number;
    audioMaxBitrate: number;
  }> = {
    hd: {
      label: 'HD',
      videoMaxBitrate: 2500000,
      videoMaxFramerate: 30,
      videoScaleDownBy: 1,
      audioMaxBitrate: 64000,
    },
    balanced: {
      label: 'Balanced',
      videoMaxBitrate: 1200000,
      videoMaxFramerate: 24,
      videoScaleDownBy: 1.2,
      audioMaxBitrate: 48000,
    },
    'data-saver': {
      label: 'Data Saver',
      videoMaxBitrate: 450000,
      videoMaxFramerate: 15,
      videoScaleDownBy: 1.8,
      audioMaxBitrate: 24000,
    },
  };

  const clearIncomingAutoMissTimeout = (callId: string) => {
    const timeoutId = incomingAutoMissTimeouts.current.get(callId);
    if (timeoutId) {
      window.clearTimeout(timeoutId);
      incomingAutoMissTimeouts.current.delete(callId);
    }
  };

  const clearDisconnectTimeout = () => {
    if (disconnectTimeoutId.current) {
      window.clearTimeout(disconnectTimeoutId.current);
      disconnectTimeoutId.current = null;
    }
  };

  const getReconnectRecoveryRate = () => {
    const { successfulRecoveries, failedRecoveries } = reconnectAnalyticsRef.current;
    const total = successfulRecoveries + failedRecoveries;
    if (total <= 0) {
      return 100;
    }
    return Number(((successfulRecoveries / total) * 100).toFixed(2));
  };

  const trackCallAnalyticsEvent = (
    name: CallAnalyticsEventName,
    metrics?: Record<string, number>,
    context?: Record<string, string | number | boolean>,
    level: 'info' | 'warn' | 'error' = 'info'
  ) => {
    const activeCall = currentCallRef.current;
    const resolvedContext: Record<string, string | number | boolean> = {
      ...(context || {}),
      callId: activeCall?.id || 'unknown',
      callType: activeCall?.type || 'unknown',
      qualityProfile: qualityProfileRef.current,
      adaptiveEnabled: isAdaptiveQualityEnabledRef.current,
      adaptiveMode: adaptiveVideoModeRef.current,
      isScreenSharing: isScreenSharingRef.current,
      reconnectAttemptsCurrentCycle: reconnectAttemptCountRef.current,
      route: typeof window !== 'undefined' ? window.location.pathname : 'unknown',
      userId: user?.uid || 'anonymous',
    };

    trackTelemetryEvent({
      name,
      level,
      metrics,
      context: resolvedContext,
    });

    if (!activeCall?.id) {
      return;
    }

    void addDoc(collection(db, 'callAnalyticsEvents'), {
      eventName: name,
      level,
      callId: activeCall.id,
      callType: activeCall.type,
      callerId: activeCall.caller.id,
      receiverId: activeCall.receiver.id,
      userId: user?.uid || null,
      qualityProfile: qualityProfileRef.current,
      adaptiveEnabled: isAdaptiveQualityEnabledRef.current,
      adaptiveMode: adaptiveVideoModeRef.current,
      metrics: metrics || {},
      context: resolvedContext,
      createdAt: serverTimestamp(),
    }).catch((error) => {
      console.warn('📞 [CallProvider] Failed to persist call analytics event:', error);
    });
  };

  const applySenderQualityProfile = async (
    profile: 'hd' | 'balanced' | 'data-saver' = qualityProfileRef.current,
    options: { silent?: boolean } = { silent: true }
  ): Promise<void> => {
    const pc = peerConnection.current;
    if (!pc) {
      return;
    }

    const preset = qualityProfileConfig[profile];
    let updatedVideoSender = false;
    let updatedAudioSender = false;

    for (const sender of pc.getSenders()) {
      if (!sender.track || typeof sender.getParameters !== 'function' || typeof sender.setParameters !== 'function') {
        continue;
      }

      try {
        const params = sender.getParameters();
        const encodings = (params.encodings && params.encodings.length > 0)
          ? [...params.encodings]
          : [{} as RTCRtpEncodingParameters];

        if (sender.track.kind === 'video') {
          params.encodings = encodings.map((encoding) => ({
            ...encoding,
            maxBitrate: preset.videoMaxBitrate,
            maxFramerate: preset.videoMaxFramerate,
            scaleResolutionDownBy: preset.videoScaleDownBy,
          }));
          await sender.setParameters(params);
          updatedVideoSender = true;
        }

        if (sender.track.kind === 'audio') {
          params.encodings = encodings.map((encoding) => ({
            ...encoding,
            maxBitrate: preset.audioMaxBitrate,
          }));
          await sender.setParameters(params);
          updatedAudioSender = true;
        }
      } catch (error) {
        console.error('📞 [CallProvider] Failed to apply sender quality profile:', error);
      }
    }

    if (!options.silent && (updatedVideoSender || updatedAudioSender)) {
      toast({
        title: `Quality profile: ${preset.label}`,
        description: updatedVideoSender
          ? `Video ${preset.videoMaxFramerate}fps at ${(preset.videoMaxBitrate / 1000).toFixed(0)}kbps`
          : `Audio ${(preset.audioMaxBitrate / 1000).toFixed(0)}kbps`,
      });
    }
  };

  const getTimestampMs = (value: unknown): number | null => {
    if (!value) return null;
    if (value instanceof Date) return value.getTime();
    // Firestore Timestamp has toDate()
    if (typeof value === 'object' && value !== null && 'toDate' in (value as any) && typeof (value as any).toDate === 'function') {
      try {
        return (value as any).toDate().getTime();
      } catch {
        return null;
      }
    }
    return null;
  };

  const getMediaConstraints = (type: 'audio' | 'video'): MediaStreamConstraints => {
    const audioConstraints: MediaTrackConstraints = {
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
      channelCount: { ideal: 1 },
      sampleRate: { ideal: 48000 },
      sampleSize: { ideal: 16 },
    };

    const videoConstraints: MediaTrackConstraints = {
      width: { ideal: isMobileDevice ? 720 : 1280, max: isMobileDevice ? 1280 : 1920 },
      height: { ideal: isMobileDevice ? 1280 : 720, max: 1920 },
      frameRate: { ideal: isMobileDevice ? 24 : 30, max: 30 },
      facingMode: { ideal: 'user' },
    };

    return {
      audio: audioConstraints,
      video: type === 'video' ? videoConstraints : false,
    };
  };

  const requestUserMedia = async (type: 'audio' | 'video'): Promise<MediaStream> => {
    const primary = getMediaConstraints(type);

    try {
      return await navigator.mediaDevices.getUserMedia(primary);
    } catch (primaryError) {
      console.warn('📞 [CallProvider] Primary getUserMedia failed, trying fallback:', primaryError);

      // Mobile-first fallback: force front camera with simpler constraints
      const mobileFrontFallback: MediaStreamConstraints = {
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
        video: type === 'video'
          ? ({
              facingMode: 'user',
              width: { ideal: 640 },
              height: { ideal: 480 },
              frameRate: { ideal: 24, max: 30 },
            } as MediaTrackConstraints)
          : false,
      };

      try {
        return await navigator.mediaDevices.getUserMedia(mobileFrontFallback);
      } catch (fallbackError) {
        console.warn('📞 [CallProvider] Front-camera fallback failed, trying minimal fallback:', fallbackError);

        const minimalFallback: MediaStreamConstraints = {
          audio: true,
          video: type === 'video' ? true : false,
        };

        return navigator.mediaDevices.getUserMedia(minimalFallback);
      }
    }
  };

  const replaceOutgoingVideoTrack = async (nextTrack: MediaStreamTrack | null): Promise<void> => {
    if (!peerConnection.current) return;

    const existingVideoSender = peerConnection.current.getSenders().find((sender) => sender.track?.kind === 'video');

    if (existingVideoSender) {
      await existingVideoSender.replaceTrack(nextTrack);
      return;
    }

    if (nextTrack) {
      const baseStream = localStreamRef.current ?? new MediaStream();
      peerConnection.current.addTrack(nextTrack, baseStream);
    }
  };

  const ensureCameraVideoTrack = async (): Promise<MediaStreamTrack | null> => {
    const existingTrack = localStreamRef.current?.getVideoTracks()[0] ?? null;
    if (existingTrack) {
      if (!existingTrack.enabled) {
        existingTrack.enabled = true;
      }
      setIsVideoOn(true);
      return existingTrack;
    }

    try {
      const cameraStream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: isMobileDevice ? 720 : 1280, max: isMobileDevice ? 1280 : 1920 },
          height: { ideal: isMobileDevice ? 1280 : 720, max: 1920 },
          frameRate: { ideal: isMobileDevice ? 24 : 30, max: 30 },
          facingMode: { ideal: 'user' },
        },
        audio: false,
      });

      const cameraTrack = cameraStream.getVideoTracks()[0] ?? null;
      if (!cameraTrack) {
        return null;
      }

      const current = localStreamRef.current;
      const merged = new MediaStream([
        ...(current?.getAudioTracks() ?? []),
        cameraTrack,
      ]);

      localStreamRef.current = merged;
      setLocalStream(merged);
      await replaceOutgoingVideoTrack(cameraTrack);
      await applySenderQualityProfile(qualityProfileRef.current, { silent: true });
      setIsVideoOn(true);
      return cameraTrack;
    } catch (error) {
      console.error('📞 [CallProvider] Failed to get camera track:', error);
      toast({
        title: 'Camera unavailable',
        description: 'Could not enable camera for this call.',
        variant: 'destructive',
      });
      return null;
    }
  };

  // Keep refs in sync for reliable cleanup
  useEffect(() => {
    localStreamRef.current = localStream;
  }, [localStream]);
  useEffect(() => {
    remoteStreamRef.current = remoteStream;
  }, [remoteStream]);
  useEffect(() => {
    screenStreamRef.current = screenStream;
  }, [screenStream]);
  useEffect(() => {
    isInCallRef.current = isInCall;
  }, [isInCall]);
  useEffect(() => {
    currentCallRef.current = currentCall;
  }, [currentCall]);
  useEffect(() => {
    incomingCallRef.current = incomingCall;
  }, [incomingCall]);
  useEffect(() => {
    isScreenSharingRef.current = isScreenSharing;
  }, [isScreenSharing]);
  useEffect(() => {
    isAdaptiveQualityEnabledRef.current = isAdaptiveQualityEnabled;
  }, [isAdaptiveQualityEnabled]);
  useEffect(() => {
    isVideoOnRef.current = isVideoOn;
  }, [isVideoOn]);
  useEffect(() => {
    adaptiveVideoModeRef.current = adaptiveVideoMode;
  }, [adaptiveVideoMode]);
  useEffect(() => {
    qualityProfileRef.current = qualityProfile;
  }, [qualityProfile]);

  useEffect(() => {
    if (!isInCall || currentCall?.status !== 'connected' || !peerConnection.current) {
      return;
    }

    void applySenderQualityProfile(qualityProfile, { silent: true });
  }, [qualityProfile, currentCall?.status, isInCall]);

  useEffect(() => {
    const hiddenRemoteVideo = remoteVideoRef.current;
    if (!hiddenRemoteVideo) return;

    if (!remoteStream || currentCall?.type !== 'audio') {
      hiddenRemoteVideo.pause();
      hiddenRemoteVideo.srcObject = null;
      return;
    }

    if (hiddenRemoteVideo.srcObject !== remoteStream) {
      hiddenRemoteVideo.srcObject = remoteStream;
    }
    hiddenRemoteVideo.muted = remoteAudioMuted;

    const playPromise = hiddenRemoteVideo.play();
    if (playPromise) {
      playPromise.catch(() => {
        // Ignore autoplay rejection; user gesture will recover playback.
      });
    }
  }, [currentCall?.type, remoteStream, remoteAudioMuted]);

  useEffect(() => {
    if (!isInCall || currentCall?.status !== 'connected' || !peerConnection.current) {
      return;
    }

    let cancelled = false;

    const sampleQuality = async () => {
      if (cancelled || !peerConnection.current) {
        return;
      }

      try {
        const stats = await peerConnection.current.getStats();
        let roundTripMs = 0;
        let jitterMs = 0;
        let packetLossPercent = 0;
        let bitrateKbps = 0;

        let inboundPackets = 0;
        let inboundLost = 0;
        let jitterSamples = 0;
        let outboundBytes = 0;

        stats.forEach((report) => {
          if (report.type === 'candidate-pair' && (report as any).state === 'succeeded') {
            const rtt = Number((report as any).currentRoundTripTime || 0);
            if (rtt > 0) {
              roundTripMs = Math.max(roundTripMs, rtt * 1000);
            }
          }

          if (report.type === 'inbound-rtp') {
            const packetsReceived = Number((report as any).packetsReceived || 0);
            const packetsLost = Number((report as any).packetsLost || 0);
            inboundPackets += Math.max(0, packetsReceived);
            inboundLost += Math.max(0, packetsLost);

            const jitter = Number((report as any).jitter || 0);
            if (jitter > 0) {
              jitterMs += jitter * 1000;
              jitterSamples += 1;
            }
          }

          if (report.type === 'outbound-rtp') {
            outboundBytes += Number((report as any).bytesSent || 0);
          }
        });

        if (jitterSamples > 0) {
          jitterMs = jitterMs / jitterSamples;
        }

        const totalPackets = inboundPackets + inboundLost;
        if (totalPackets > 0) {
          packetLossPercent = (inboundLost / totalPackets) * 100;
        }

        const baseline = qualityBaselineRef.current;
        const now = Date.now();
        if (baseline.at > 0 && outboundBytes >= baseline.bytes && now > baseline.at) {
          const deltaBytes = outboundBytes - baseline.bytes;
          const deltaSeconds = (now - baseline.at) / 1000;
          bitrateKbps = deltaSeconds > 0 ? (deltaBytes * 8) / 1000 / deltaSeconds : 0;
        }
        qualityBaselineRef.current = { bytes: outboundBytes, at: now };

        let score = 100;
        score -= Math.min(40, packetLossPercent * 10);
        score -= Math.min(28, roundTripMs / 12);
        score -= Math.min(20, jitterMs / 3);
        if (bitrateKbps > 0 && bitrateKbps < 350) {
          score -= Math.min(15, (350 - bitrateKbps) / 18);
        }
        score = Math.max(1, Math.round(score));

        let status: 'excellent' | 'good' | 'fair' | 'poor' = 'excellent';
        if (score < 45) status = 'poor';
        else if (score < 65) status = 'fair';
        else if (score < 84) status = 'good';

        const recommendations: string[] = [];
        if (packetLossPercent >= 3) {
          recommendations.push('High packet loss detected. Try switching to a stronger network.');
        }
        if (roundTripMs >= 180) {
          recommendations.push('Latency is elevated. Close bandwidth-heavy apps for smoother calls.');
        }
        if (jitterMs >= 30) {
          recommendations.push('Jitter is unstable. Prefer steady Wi-Fi or a stable 5G signal.');
        }
        if (bitrateKbps > 0 && bitrateKbps < 280) {
          recommendations.push('Low uplink bitrate. Turning off video can improve call stability.');
        }
        if (recommendations.length === 0) {
          recommendations.push('Call quality is stable. No action needed.');
        }

        const isPoorNetwork =
          status === 'poor'
          || packetLossPercent >= 6
          || roundTripMs >= 320
          || (bitrateKbps > 0 && bitrateKbps < 220);
        const isGoodNetwork =
          status === 'excellent'
          || (status === 'good' && packetLossPercent < 1.5 && roundTripMs < 170 && jitterMs < 25);

        if (isPoorNetwork) {
          qualityTrendRef.current.poorStreak += 1;
          qualityTrendRef.current.goodStreak = 0;
        } else if (isGoodNetwork) {
          qualityTrendRef.current.goodStreak += 1;
          qualityTrendRef.current.poorStreak = Math.max(0, qualityTrendRef.current.poorStreak - 1);
        } else {
          qualityTrendRef.current.poorStreak = Math.max(0, qualityTrendRef.current.poorStreak - 1);
          qualityTrendRef.current.goodStreak = Math.max(0, qualityTrendRef.current.goodStreak - 1);
        }

        if (
          isAdaptiveQualityEnabledRef.current
          && currentCallRef.current?.type === 'video'
          && !isScreenSharingRef.current
        ) {
          const nowMs = Date.now();
          const secondsSinceLastSwitch = (nowMs - qualityTrendRef.current.lastAutoSwitchAt) / 1000;

          if (
            qualityTrendRef.current.poorStreak >= 3
            && isVideoOnRef.current
            && adaptiveVideoModeRef.current === 'normal'
            && secondsSinceLastSwitch >= 15
          ) {
            const videoTrack = localStreamRef.current?.getVideoTracks()[0] ?? null;
            if (videoTrack) {
              videoTrack.enabled = false;
              setIsVideoOn(false);
              setAdaptiveVideoMode('auto-audio-only');
              qualityTrendRef.current.lastAutoSwitchAt = nowMs;
              trackCallAnalyticsEvent(
                'call.adaptive.triggered',
                {
                  score,
                  packetLossPercent,
                  rttMs: roundTripMs,
                  jitterMs,
                  bitrateKbps,
                  poorStreak: qualityTrendRef.current.poorStreak,
                },
                {
                  reason: 'network_degradation',
                },
                'warn'
              );
              toast({
                title: 'Adaptive quality engaged',
                description: 'Video paused automatically to keep audio stable.',
              });
            }
          }

          if (
            qualityTrendRef.current.goodStreak >= 4
            && adaptiveVideoModeRef.current === 'auto-audio-only'
            && secondsSinceLastSwitch >= 15
          ) {
            const restoredTrack = await ensureCameraVideoTrack();
            if (restoredTrack) {
              restoredTrack.enabled = true;
              setIsVideoOn(true);
              setAdaptiveVideoMode('normal');
              qualityTrendRef.current.lastAutoSwitchAt = nowMs;
              trackCallAnalyticsEvent(
                'call.adaptive.recovered',
                {
                  score,
                  packetLossPercent,
                  rttMs: roundTripMs,
                  jitterMs,
                  bitrateKbps,
                  goodStreak: qualityTrendRef.current.goodStreak,
                },
                {
                  reason: 'network_stabilized',
                }
              );
              toast({
                title: 'Adaptive quality recovered',
                description: 'Video resumed after network stabilized.',
              });
            }
          }
        }

        if (!cancelled) {
          setCallQuality({
            status,
            score,
            rttMs: Number(roundTripMs.toFixed(1)),
            jitterMs: Number(jitterMs.toFixed(1)),
            packetLossPercent: Number(packetLossPercent.toFixed(2)),
            bitrateKbps: Number(bitrateKbps.toFixed(0)),
            recommendations: recommendations.slice(0, 2),
            sampledAt: now,
          });
        }
      } catch {
        // Ignore intermittent stats sampling errors.
      }
    };

    void sampleQuality();
    const intervalId = window.setInterval(() => {
      void sampleQuality();
    }, 3000);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [currentCall?.status, isInCall]);

  // Initialize WebRTC peer connection
  const createPeerConnection = (callId: string) => {
    const pc = new RTCPeerConnection(rtcConfig);

    const tryReconnectWithAnalytics = (reason: 'connection_disconnected' | 'connection_failed' | 'ice_failed') => {
      if (reconnectAttemptCountRef.current >= 3) {
        return;
      }

      reconnectAttemptCountRef.current += 1;
      reconnectAnalyticsRef.current.attempts += 1;

      trackCallAnalyticsEvent(
        'call.reconnect.attempt',
        {
          attemptNumberCurrentCycle: reconnectAttemptCountRef.current,
          attemptsTotal: reconnectAnalyticsRef.current.attempts,
          successfulRecoveries: reconnectAnalyticsRef.current.successfulRecoveries,
          failedRecoveries: reconnectAnalyticsRef.current.failedRecoveries,
          recoverySuccessRatePercent: getReconnectRecoveryRate(),
        },
        {
          reason,
          connectionState: pc.connectionState,
          iceState: pc.iceConnectionState,
        },
        'warn'
      );

      try {
        pc.restartIce();
      } catch (error) {
        console.error('📞 [CallProvider] Failed to restart ICE:', error);
      }
    };

    const scheduleDisconnectGuard = () => {
      if (disconnectTimeoutId.current) {
        return;
      }

      disconnectTimeoutId.current = window.setTimeout(() => {
        const state = pc.connectionState;
        if (state === 'disconnected' || state === 'failed') {
          reconnectAnalyticsRef.current.failedRecoveries += 1;
          trackCallAnalyticsEvent(
            'call.reconnect.failed',
            {
              attemptsTotal: reconnectAnalyticsRef.current.attempts,
              successfulRecoveries: reconnectAnalyticsRef.current.successfulRecoveries,
              failedRecoveries: reconnectAnalyticsRef.current.failedRecoveries,
              recoverySuccessRatePercent: getReconnectRecoveryRate(),
            },
            {
              reason: 'disconnect_guard_timeout',
              connectionState: state,
              iceState: pc.iceConnectionState,
            },
            'error'
          );
          toast({
            title: 'Connection lost',
            description: 'Call ended after network recovery timed out.',
            variant: 'destructive',
          });
          void endCall();
        }
        clearDisconnectTimeout();
      }, 12000);
    };
    
    // Handle ICE candidates
    pc.onicecandidate = async (event) => {
      if (event.candidate) {
        console.log('📞 [CallProvider] Generated ICE candidate:', event.candidate.type);
        try {
          const callDoc = doc(db, 'calls', callId);
          const callSnapshot = await getDoc(callDoc);
          
          if (callSnapshot.exists()) {
            const isCallerField = callSnapshot.data().callerId === user?.uid ? 'callerCandidates' : 'receiverCandidates';
            console.log('📞 [CallProvider] Saving ICE candidate to Firestore:', isCallerField);
            await updateDoc(callDoc, {
              [isCallerField]: arrayUnion({
                candidate: event.candidate.candidate,
                sdpMid: event.candidate.sdpMid,
                sdpMLineIndex: event.candidate.sdpMLineIndex
              })
            });
            console.log('📞 [CallProvider] ✅ ICE candidate saved');
          }
        } catch (error) {
          console.error('📞 [CallProvider] ❌ Error adding ICE candidate:', error);
        }
      } else {
        console.log('📞 [CallProvider] ICE gathering complete (null candidate)');
      }
    };
    
    // Handle remote stream - IMPROVED
    pc.ontrack = (event) => {
      console.log('📞 [CallProvider] ✅ Received remote track:', {
        kind: event.track.kind,
        streams: event.streams.length,
        streamId: event.streams[0]?.id
      });
      
      if (event.streams && event.streams[0]) {
        console.log('📞 [CallProvider] Setting remote stream');
        setRemoteStream(event.streams[0]);
        
        // Play connected sound
        callSounds.playConnectedSound();
      }
    };

    // Handle connection state changes
    pc.onconnectionstatechange = () => {
      console.log('📞 [CallProvider] Connection state:', pc.connectionState);
      if (pc.connectionState === 'connected') {
        console.log('📞 [CallProvider] ✅ WebRTC connection established!');
        if (reconnectAttemptCountRef.current > 0) {
          reconnectAnalyticsRef.current.successfulRecoveries += 1;
          trackCallAnalyticsEvent(
            'call.reconnect.recovered',
            {
              attemptsNeeded: reconnectAttemptCountRef.current,
              attemptsTotal: reconnectAnalyticsRef.current.attempts,
              successfulRecoveries: reconnectAnalyticsRef.current.successfulRecoveries,
              failedRecoveries: reconnectAnalyticsRef.current.failedRecoveries,
              recoverySuccessRatePercent: getReconnectRecoveryRate(),
            },
            {
              reason: 'connection_state_connected',
            }
          );
        }
        reconnectAttemptCountRef.current = 0;
        clearDisconnectTimeout();
        setCurrentCall(prev => prev ? { ...prev, status: 'connected' } : null);
      } else if (pc.connectionState === 'disconnected') {
        console.log('📞 [CallProvider] Connection temporarily disconnected, attempting recovery...');
        scheduleDisconnectGuard();
        tryReconnectWithAnalytics('connection_disconnected');
      } else if (pc.connectionState === 'failed') {
        console.log('📞 [CallProvider] ❌ Connection failed, attempting ICE restart before hangup');
        scheduleDisconnectGuard();
        tryReconnectWithAnalytics('connection_failed');
      } else if (pc.connectionState === 'closed') {
        clearDisconnectTimeout();
      }
    };

    // Handle ICE connection state
    pc.oniceconnectionstatechange = () => {
      console.log('📞 [CallProvider] ICE connection state:', pc.iceConnectionState);
      if (pc.iceConnectionState === 'failed') {
        console.log('📞 [CallProvider] ICE failed, attempting restart...');
        // Attempt ICE restart
        tryReconnectWithAnalytics('ice_failed');
      } else if (pc.iceConnectionState === 'connected') {
        console.log('📞 [CallProvider] ✅ ICE connection established!');
        reconnectAttemptCountRef.current = 0;
        clearDisconnectTimeout();
      }
    };
    
    return pc;
  };

  // Listen for incoming calls
  useEffect(() => {
    if (!enabled) {
      return;
    }

    if (!user) {
      return;
    }

    const callsQuery = query(
      collection(db, 'calls'),
      where('receiverId', '==', user.uid),
      where('status', '==', 'ringing')
    );

    incomingCallsUnsubscribe.current = onSnapshot(callsQuery, (snapshot) => {
      snapshot.docChanges().forEach((change) => {
        const data = change.doc.data();

        // If a ringing call leaves the query (status changes), ensure we stop ringing UI/sounds.
        if (change.type === 'removed') {
          const removedCallId = change.doc.id;
          clearIncomingAutoMissTimeout(removedCallId);
          setIncomingCall(prev => {
            if (prev?.id === removedCallId) {
              callSounds.stopAll();
              return null;
            }
            return prev;
          });
          return;
        }

        if (change.type === 'added') {
          console.log('📞 [CallProvider] ✅ NEW INCOMING CALL DETECTED:', {
            callId: change.doc.id,
            caller: data.callerName,
            type: data.type,
            status: data.status
          });

          // Ignore/cleanup stale ringing calls (e.g. app crashed before marking missed).
          const createdAtMs = getTimestampMs(data.createdAt) ?? getTimestampMs(data.startTime);
          if (createdAtMs && Date.now() - createdAtMs > 2 * 60 * 1000) {
            console.log('📞 [CallProvider] Stale ringing call detected; auto-marking missed:', change.doc.id);
            updateDoc(doc(db, 'calls', change.doc.id), {
              status: 'missed',
              endTime: serverTimestamp(),
            }).catch((err) => console.error('📞 [CallProvider] Failed to auto-miss stale call:', err));
            return;
          }

          // If we are already in a call, auto-reject as busy.
          if (isInCallRef.current || currentCallRef.current) {
            console.log('📞 [CallProvider] Already in a call; auto-rejecting incoming call:', change.doc.id);
            updateDoc(doc(db, 'calls', change.doc.id), {
              status: 'rejected',
              endTime: serverTimestamp(),
            }).catch((err) => console.error('📞 [CallProvider] Failed to auto-reject busy call:', err));
            return;
          }

          // De-dupe the same incoming call.
          if (incomingCallRef.current?.id === change.doc.id) {
            console.log('📞 [CallProvider] Incoming call already set; skipping duplicate:', change.doc.id);
            return;
          }
          
          const incomingCallData: CallData = {
            id: change.doc.id,
            caller: {
              id: data.callerId,
              name: data.callerName,
              avatar: data.callerAvatar
            },
            receiver: {
              id: data.receiverId,
              name: data.receiverName,
              avatar: data.receiverAvatar
            },
            type: data.type,
            status: 'ringing'
          };
          setIncomingCall(incomingCallData);
          
          // Play ringtone with modern sound system
          console.log('📞 [CallProvider] Playing ringtone...');
          callSounds.playIncomingRingtone();

          // Auto-mark missed if not answered within 60 seconds (receiver-side safety net).
          clearIncomingAutoMissTimeout(change.doc.id);
          const timeoutId = window.setTimeout(async () => {
            try {
              const snap = await getDoc(doc(db, 'calls', change.doc.id));
              if (snap.exists() && snap.data().status === 'ringing') {
                await updateDoc(doc(db, 'calls', change.doc.id), {
                  status: 'missed',
                  endTime: serverTimestamp(),
                });
              }
            } catch (err) {
              console.error('📞 [CallProvider] Error auto-marking missed incoming call:', err);
            } finally {
              clearIncomingAutoMissTimeout(change.doc.id);
              setIncomingCall(prev => (prev?.id === change.doc.id ? null : prev));
              callSounds.stopAll();
            }
          }, 60000);
          incomingAutoMissTimeouts.current.set(change.doc.id, timeoutId);
        }

        // Keep the incomingCall object up-to-date if Firestore updates while still ringing.
        if (change.type === 'modified') {
          setIncomingCall(prev => {
            if (!prev || prev.id !== change.doc.id) return prev;
            return {
              ...prev,
              caller: {
                ...prev.caller,
                name: data.callerName ?? prev.caller.name,
                avatar: data.callerAvatar ?? prev.caller.avatar,
              },
              receiver: {
                ...prev.receiver,
                name: data.receiverName ?? prev.receiver.name,
                avatar: data.receiverAvatar ?? prev.receiver.avatar,
              },
              type: data.type ?? prev.type,
            };
          });
        }
      });
    }, (error) => {
      const errorCode = String((error as any)?.code || '').toLowerCase();
      if (!errorCode.includes('permission-denied')) {
        console.error('📞 [CallProvider] ❌ Error in incoming call listener:', error);
      }
      setIncomingCall(null);
      callSounds.stopAll();
    });

    return () => {
      if (incomingCallsUnsubscribe.current) {
        incomingCallsUnsubscribe.current();
      }
    };
  }, [user?.uid, enabled]);

  const startCall = async (receiverId: string, receiverName: string, receiverAvatar: string, type: 'audio' | 'video') => {
    if (!user) {
      console.error('📞 [CallProvider] Cannot start call: No user logged in');
      return;
    }

    if (isInCallRef.current) {
      toast({
        title: 'Already in a call',
        description: 'End your current call before starting a new one.',
        variant: 'destructive',
      });
      return;
    }
    
    console.log('📞 [CallProvider] ===== STARTING CALL =====');
    console.log('📞 [CallProvider] Receiver ID:', receiverId);
    console.log('📞 [CallProvider] Receiver Name:', receiverName);
    console.log('📞 [CallProvider] Call Type:', type);
    console.log('📞 [CallProvider] Caller ID:', user.uid);
    console.log('📞 [CallProvider] Caller Name:', user.displayName);
    
    // Clear processed candidates for new call
    processedCandidates.current.clear();
    
    try {
      // Request media permissions
      console.log('📞 [CallProvider] Requesting media permissions...');
      const stream = await requestUserMedia(type);
      
      console.log('📞 [CallProvider] ✅ Media permissions granted, tracks:', stream.getTracks().map(t => ({ kind: t.kind, enabled: t.enabled })));
      setLocalStream(stream);
      setIsVideoOn(type === 'video');
      
      // Create call document in Firestore
      console.log('📞 [CallProvider] Creating call document in Firestore...');
      const callsRef = collection(db, 'calls');
      const callDocRef = await addDoc(callsRef, {
        callerId: user.uid,
        callerName: user.displayName || 'Unknown',
        callerAvatar: user.photoURL || '',
        caller: {
          id: user.uid,
          name: user.displayName || 'Unknown',
          avatar: user.photoURL || ''
        },
        receiverId,
        receiverName,
        receiverAvatar,
        receiver: {
          id: receiverId,
          name: receiverName,
          avatar: receiverAvatar
        },
        type,
        status: 'ringing',
        offer: null,
        answer: null,
        callerCandidates: [],
        receiverCandidates: [],
        createdAt: serverTimestamp(),
        startTime: serverTimestamp(),
        endTime: null
      });

      const callId = callDocRef.id;
      console.log('📞 [CallProvider] ✅ Call document created with ID:', callId);
      
      // Create call data
      const callData: CallData = {
        id: callId,
        caller: {
          id: user.uid,
          name: user.displayName || 'Unknown',
          avatar: user.photoURL || ''
        },
        receiver: {
          id: receiverId,
          name: receiverName,
          avatar: receiverAvatar
        },
        type,
        status: 'ringing',
        startTime: new Date()
      };
      
      setCurrentCall(callData);
      setIsInCall(true);
      setAdaptiveVideoMode('normal');
      setQualityProfileState('balanced');
      qualityTrendRef.current = { poorStreak: 0, goodStreak: 0, lastAutoSwitchAt: 0 };
      reconnectAttemptCountRef.current = 0;
      reconnectAnalyticsRef.current = {
        attempts: 0,
        successfulRecoveries: 0,
        failedRecoveries: 0,
      };
      clearDisconnectTimeout();
      
      // Create peer connection
      console.log('📞 [CallProvider] Creating peer connection...');
      peerConnection.current = createPeerConnection(callId);
      
      // Add local stream tracks to peer connection
      console.log('📞 [CallProvider] Adding local tracks to peer connection...');
      stream.getTracks().forEach(track => {
        console.log('📞 [CallProvider] Adding track:', track.kind);
        peerConnection.current?.addTrack(track, stream);
      });
      await applySenderQualityProfile(qualityProfileRef.current, { silent: true });
      
      // Create and set local offer
      console.log('📞 [CallProvider] Creating WebRTC offer...');
      const offer = await peerConnection.current.createOffer();
      await peerConnection.current.setLocalDescription(offer);
      console.log('📞 [CallProvider] ✅ Offer created and set as local description');
      
      // Save offer to Firestore
      console.log('📞 [CallProvider] Saving offer to Firestore...');
      await updateDoc(doc(db, 'calls', callId), {
        offer: {
          type: offer.type,
          sdp: offer.sdp
        }
      });
      console.log('📞 [CallProvider] ✅ Offer saved to Firestore');
      
      // Send notification to receiver
      console.log('📞 [CallProvider] Sending notification to receiver...');
      await addNotification(receiverId, 'call', {
        callId,
        callType: type,
        senderName: user.displayName || 'Someone',
        senderPhoto: user.photoURL || '',
        senderId: user.uid,
        actionUrl: `/calls/${callId}`,
      });
      
      console.log('📞 [CallProvider] ✅ Call initiated successfully!');
      
      // Play outgoing ringtone
      callSounds.playOutgoingRingtone();
      
      toast({
        title: `${type === 'video' ? 'Video' : 'Audio'} call started`,
        description: `Calling ${receiverName}...`,
      });
      
      // Listen for answer from receiver
      callUnsubscribe.current = onSnapshot(doc(db, 'calls', callId), async (snapshot) => {
        const data = snapshot.data();
        if (!data) {
          console.log('📞 [CallProvider] Call document snapshot is empty');
          return;
        }
        
        console.log('📞 [CallProvider] Call document updated:', {
          hasAnswer: !!data.answer,
          hasRemoteDescription: !!peerConnection.current?.remoteDescription,
          receiverCandidatesCount: data.receiverCandidates?.length || 0,
          status: data.status
        });
        
        // Handle answer
        if (data.answer && !peerConnection.current?.remoteDescription) {
          console.log('📞 [CallProvider] Received answer from receiver, setting remote description...');
          const answerDescription = new RTCSessionDescription(data.answer);
          await peerConnection.current?.setRemoteDescription(answerDescription);
          console.log('📞 [CallProvider] ✅ Remote description set');
        }
        
        // Handle receiver's ICE candidates
        if (data.receiverCandidates && peerConnection.current) {
          console.log('📞 [CallProvider] Processing receiver ICE candidates:', data.receiverCandidates.length);
          for (const candidateData of data.receiverCandidates) {
            const candidateKey = `${candidateData.candidate}-${candidateData.sdpMid}-${candidateData.sdpMLineIndex}`;
            
            // Skip if already processed
            if (processedCandidates.current.has(candidateKey)) {
              continue;
            }
            
            try {
              await peerConnection.current.addIceCandidate(
                new RTCIceCandidate(candidateData)
              );
              processedCandidates.current.add(candidateKey);
              console.log('📞 [CallProvider] ✅ Added receiver ICE candidate');
            } catch (error) {
              console.error('📞 [CallProvider] ❌ Error adding received ICE candidate:', error);
            }
          }
        }
        
        // Handle status changes
        if (data.status === 'rejected') {
          toast({
            title: 'Call rejected',
            description: `${receiverName} declined the call.`,
            variant: 'destructive'
          });
          endCall();
        } else if (data.status === 'connected') {
          setCurrentCall(prev => prev ? { ...prev, status: 'connected', startTime: new Date() } : null);
          callSounds.stopAll();
          toast({
            title: 'Call connected',
            description: `Connected to ${receiverName}`,
          });

          if (pendingStartScreenShareRef.current) {
            pendingStartScreenShareRef.current = false;
            void toggleScreenShare();
          }
        } else if (data.status === 'ended' || data.status === 'missed') {
          endCall();
        }
      });
      
      // Auto-end if no answer within 60 seconds
      if (callTimeoutId.current) {
        window.clearTimeout(callTimeoutId.current);
      }
      callTimeoutId.current = window.setTimeout(async () => {
        const callSnapshot = await getDoc(doc(db, 'calls', callId));
        if (callSnapshot.exists() && callSnapshot.data().status === 'ringing') {
          await updateDoc(doc(db, 'calls', callId), {
            status: 'missed',
            endTime: serverTimestamp()
          });
          toast({
            title: 'No answer',
            description: `${receiverName} didn't answer`,
            variant: 'destructive'
          });
          endCall();
        }
      }, 60000);
      
    } catch (error: any) {
      console.error('📞 [CallProvider] ❌ Error starting call:', error);
      console.error('📞 [CallProvider] Error details:', {
        message: error.message,
        code: error.code,
        stack: error.stack
      });
      toast({
        title: 'Call failed',
        description: error.message || 'Could not start the call. Please check your permissions.',
        variant: 'destructive'
      });
      endCall();
    }
  };

  const startScreenShareCall = async (receiverId: string, receiverName: string, receiverAvatar: string) => {
    if (isInCallRef.current) {
      const activePeerId = currentCallRef.current
        ? (currentCallRef.current.caller.id === user?.uid
            ? currentCallRef.current.receiver.id
            : currentCallRef.current.caller.id)
        : null;

      if (activePeerId === receiverId) {
        await toggleScreenShare();
        return;
      }

      toast({
        title: 'Already in a call',
        description: 'End your current call before starting screen sharing with someone else.',
        variant: 'destructive',
      });
      return;
    }

    pendingStartScreenShareRef.current = true;
    await startCall(receiverId, receiverName, receiverAvatar, 'video');
  };

  const acceptCall = async (callId: string) => {
    if (!user) {
      console.error('📞 [CallProvider] Cannot accept call: No user logged in');
      return;
    }
    
    console.log('📞 [CallProvider] ===== ACCEPTING CALL =====');
    console.log('📞 [CallProvider] Call ID:', callId);
    console.log('📞 [CallProvider] User:', user.uid);
    
    // Clear processed candidates for new call
    processedCandidates.current.clear();
    
    // Stop ringtone immediately
    callSounds.stopAll();

    clearIncomingAutoMissTimeout(callId);
    
    try {
      const callDoc = doc(db, 'calls', callId);
      const callSnapshot = await getDoc(callDoc);
      
      if (!callSnapshot.exists()) {
        console.error('📞 [CallProvider] Call document not found!');
        throw new Error('Call not found');
      }
      
      const callData = callSnapshot.data();
      console.log('📞 [CallProvider] Call data retrieved:', {
        type: callData.type,
        callerId: callData.callerId,
        hasOffer: !!callData.offer
      });
      
      // Request media permissions
      console.log('📞 [CallProvider] Requesting media permissions...');
      const stream = await requestUserMedia(callData.type);
      
      console.log('📞 [CallProvider] ✅ Media permissions granted, tracks:', stream.getTracks().map(t => ({ kind: t.kind, enabled: t.enabled })));
      setLocalStream(stream);
      setIsVideoOn(callData.type === 'video');
      setIncomingCall(null);
      
      // Create call data
      const acceptedCallData: CallData = {
        id: callId,
        caller: {
          id: callData.callerId,
          name: callData.callerName,
          avatar: callData.callerAvatar
        },
        receiver: {
          id: callData.receiverId,
          name: callData.receiverName,
          avatar: callData.receiverAvatar
        },
        type: callData.type,
        status: 'connected',
        startTime: new Date()
      };
      
      setCurrentCall(acceptedCallData);
      setIsInCall(true);
      setAdaptiveVideoMode('normal');
      setQualityProfileState('balanced');
      qualityTrendRef.current = { poorStreak: 0, goodStreak: 0, lastAutoSwitchAt: 0 };
      reconnectAttemptCountRef.current = 0;
      reconnectAnalyticsRef.current = {
        attempts: 0,
        successfulRecoveries: 0,
        failedRecoveries: 0,
      };
      clearDisconnectTimeout();
      
      // Create peer connection
      console.log('📞 [CallProvider] Creating peer connection...');
      peerConnection.current = createPeerConnection(callId);
      
      // Add local stream tracks
      console.log('📞 [CallProvider] Adding local tracks to peer connection...');
      stream.getTracks().forEach(track => {
        console.log('📞 [CallProvider] Adding track:', track.kind);
        peerConnection.current?.addTrack(track, stream);
      });
      await applySenderQualityProfile(qualityProfileRef.current, { silent: true });
      
      // Set remote description (offer from caller)
      console.log('📞 [CallProvider] Setting remote description (offer)...');
      const offer = new RTCSessionDescription(callData.offer);
      await peerConnection.current.setRemoteDescription(offer);
      console.log('📞 [CallProvider] ✅ Remote description set');
      
      // Create and set local answer
      console.log('📞 [CallProvider] Creating answer...');
      const answer = await peerConnection.current.createAnswer();
      await peerConnection.current.setLocalDescription(answer);
      console.log('📞 [CallProvider] ✅ Answer created and set as local description');
      
      // Save answer and update status
      console.log('📞 [CallProvider] Saving answer to Firestore...');
      await updateDoc(callDoc, {
        answer: {
          type: answer.type,
          sdp: answer.sdp
        },
        status: 'connected',
        startTime: serverTimestamp()
      });
      console.log('📞 [CallProvider] ✅ Answer saved to Firestore');
      
      // Listen for caller's ICE candidates
      callUnsubscribe.current = onSnapshot(callDoc, async (snapshot) => {
        const data = snapshot.data();
        if (!data) {
          console.log('📞 [CallProvider] Call document snapshot is empty');
          return;
        }
        
        console.log('📞 [CallProvider] Call document updated:', {
          callerCandidatesCount: data.callerCandidates?.length || 0,
          status: data.status
        });
        
        // Handle caller's ICE candidates
        if (data.callerCandidates && peerConnection.current) {
          console.log('📞 [CallProvider] Processing caller ICE candidates:', data.callerCandidates.length);
          for (const candidateData of data.callerCandidates) {
            const candidateKey = `${candidateData.candidate}-${candidateData.sdpMid}-${candidateData.sdpMLineIndex}`;
            
            // Skip if already processed
            if (processedCandidates.current.has(candidateKey)) {
              continue;
            }
            
            try {
              await peerConnection.current.addIceCandidate(
                new RTCIceCandidate(candidateData)
              );
              processedCandidates.current.add(candidateKey);
              console.log('📞 [CallProvider] ✅ Added caller ICE candidate');
            } catch (error) {
              console.error('📞 [CallProvider] ❌ Error adding caller ICE candidate:', error);
            }
          }
        }

        // If caller ends/cancels, close locally
        if (data.status === 'ended' || data.status === 'missed' || data.status === 'rejected') {
          endCall();
        }
      });
      
      toast({
        title: 'Call accepted',
        description: 'You are now connected.',
      });
      
    } catch (error: any) {
      console.error('Error accepting call:', error);
      toast({
        title: 'Failed to accept call',
        description: error.message,
        variant: 'destructive'
      });
      rejectCall(callId);
    }
  };

  const rejectCall = async (callId: string) => {
    console.log('📞 [CallProvider] Rejecting call:', callId);
    
    try {
      // Stop ringtone
      callSounds.stopAll();

      clearIncomingAutoMissTimeout(callId);
      
      await updateDoc(doc(db, 'calls', callId), {
        status: 'rejected',
        endTime: serverTimestamp()
      });
      
      setIncomingCall(null);
      
      // Play busy sound
      callSounds.playBusySound();
      
      toast({
        title: 'Call rejected',
        description: 'The call has been rejected.',
      });
    } catch (error) {
      console.error('📞 [CallProvider] Error rejecting call:', error);
    }
  };

  const endCall = async () => {
    console.log('📞 [CallProvider] Ending call...');
    
    // Stop all sounds immediately
    callSounds.stopAll();

    if (callTimeoutId.current) {
      window.clearTimeout(callTimeoutId.current);
      callTimeoutId.current = null;
    }
    clearDisconnectTimeout();

    if (incomingCall?.id) {
      clearIncomingAutoMissTimeout(incomingCall.id);
    }
    pendingStartScreenShareRef.current = false;
    setIncomingCall(null);
    
    try {
      const reconnectSummaryBeforeReset = {
        ...reconnectAnalyticsRef.current,
      };

      const durationSeconds = currentCall?.startTime
        ? Math.max(0, Math.floor((Date.now() - new Date(currentCall.startTime).getTime()) / 1000))
        : 0;

      trackCallAnalyticsEvent(
        'call.reconnect.summary',
        {
          attemptsTotal: reconnectSummaryBeforeReset.attempts,
          successfulRecoveries: reconnectSummaryBeforeReset.successfulRecoveries,
          failedRecoveries: reconnectSummaryBeforeReset.failedRecoveries,
          recoverySuccessRatePercent: getReconnectRecoveryRate(),
          callDurationSeconds: durationSeconds,
        },
        {
          terminatedBy: user?.uid === currentCall?.caller.id ? 'caller' : 'receiver_or_remote',
        }
      );

      // Update call document
      if (currentCall?.id) {
        const callDoc = doc(db, 'calls', currentCall.id);
        const callSnapshot = await getDoc(callDoc);
        
        if (callSnapshot.exists()) {
          const callData = callSnapshot.data();
          const endTime = new Date();
          const startTime = callData.startTime?.toDate() || new Date();
          const duration = Math.floor((endTime.getTime() - startTime.getTime()) / 1000);

          const nextStatus: CallData['status'] =
            callData.status === 'missed' || callData.status === 'rejected' || callData.status === 'ended'
              ? callData.status
              : 'ended';
          
          await updateDoc(callDoc, {
              status: nextStatus,
            endTime: serverTimestamp(),
            duration
          });
          
          // Save to call history
          await addDoc(collection(db, 'callHistory'), {
            callId: currentCall.id,
            callerId: currentCall.caller.id,
            callerName: currentCall.caller.name,
            receiverId: currentCall.receiver.id,
            receiverName: currentCall.receiver.name,
            type: currentCall.type,
            status: callData.status,
            duration,
            timestamp: serverTimestamp()
          });
        }
      }
    } catch (error) {
      console.error('📞 [CallProvider] Error updating call document:', error);
    }
    
    // Force cleanup everything
    console.log('📞 [CallProvider] Cleaning up call resources...');
    
    // Stop local stream
    if (localStream) {
      localStream.getTracks().forEach(track => {
        track.stop();
        console.log('📞 [CallProvider] Stopped local track:', track.kind);
      });
      setLocalStream(null);
    }
    
    // Stop remote stream
    if (remoteStream) {
      remoteStream.getTracks().forEach(track => {
        track.stop();
      });
      setRemoteStream(null);
    }
    
    // Stop screen share
    if (screenStream) {
      screenStream.getTracks().forEach(track => track.stop());
      setScreenStream(null);
      setIsScreenSharing(false);
    }
    cameraTrackBeforeShareRef.current = null;
    
    // Close peer connection
    if (peerConnection.current) {
      peerConnection.current.close();
      peerConnection.current = null;
      console.log('📞 [CallProvider] Peer connection closed');
    }
    
    // Unsubscribe from call updates
    if (callUnsubscribe.current) {
      callUnsubscribe.current();
      callUnsubscribe.current = null;
    }
    
    setCurrentCall(null);
    setIsInCall(false);
    setCallQuality(null);
    setIsCallMinimized(false);
    setRemoteStream(null);
    setIsMuted(false);
    setIsVideoOn(true);
    setRemoteAudioMuted(false);
    setAdaptiveVideoMode('normal');
    setQualityProfileState('balanced');
    qualityTrendRef.current = { poorStreak: 0, goodStreak: 0, lastAutoSwitchAt: 0 };
    reconnectAttemptCountRef.current = 0;
    reconnectAnalyticsRef.current = {
      attempts: 0,
      successfulRecoveries: 0,
      failedRecoveries: 0,
    };
    
    // Play ended sound
    callSounds.playEndedSound();
    
    console.log('📞 [CallProvider] ✅ Call ended successfully');
    
    toast({
      title: 'Call ended',
      description: 'The call has been ended.',
    });
  };

  const toggleMute = () => {
    if (localStream) {
      const audioTrack = localStream.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setIsMuted(!audioTrack.enabled);
      }
    }
  };

  const toggleVideo = () => {
    const current = localStreamRef.current;
    const videoTrack = current?.getVideoTracks()[0];

    if (videoTrack) {
      videoTrack.enabled = !videoTrack.enabled;
      setIsVideoOn(videoTrack.enabled);
      return;
    }

    if (!isVideoOn) {
      void ensureCameraVideoTrack();
    }
  };

  const toggleRemoteAudio = () => {
    setRemoteAudioMuted((prev) => {
      const next = !prev;
      toast({
        title: next ? 'Call audio muted' : 'Call audio unmuted',
        description: next ? 'Remote participant audio is muted on this device.' : 'Remote participant audio is back on.',
      });
      return next;
    });
  };

  const toggleAdaptiveQuality = () => {
    setIsAdaptiveQualityEnabled((prev) => {
      const next = !prev;
      if (!next) {
        qualityTrendRef.current = { poorStreak: 0, goodStreak: 0, lastAutoSwitchAt: 0 };
        setAdaptiveVideoMode('normal');
      }

      toast({
        title: next ? 'Adaptive quality enabled' : 'Adaptive quality disabled',
        description: next
          ? 'Video will auto-adjust during unstable network conditions.'
          : 'Call quality adjustments are now manual.',
      });

      return next;
    });
  };

  const setQualityProfile = async (profile: 'hd' | 'balanced' | 'data-saver') => {
    setQualityProfileState(profile);

    if (!peerConnection.current || !isInCallRef.current || currentCallRef.current?.status !== 'connected') {
      return;
    }

    await applySenderQualityProfile(profile, { silent: false });
  };

  const toggleScreenShare = async () => {
    try {
      if (!canScreenShare) {
        toast({
          title: 'Screen share not supported',
          description: 'Your current device or browser does not support screen sharing.',
          variant: 'destructive',
        });
        return;
      }

      if (!isInCallRef.current || !peerConnection.current) {
        toast({
          title: 'No active call',
          description: 'Start a call before sharing your screen.',
          variant: 'destructive',
        });
        return;
      }

      if (isScreenSharingRef.current) {
        // Stop screen sharing
        if (screenStreamRef.current) {
          screenStreamRef.current.getTracks().forEach(track => track.stop());
          setScreenStream(null);
        }

        const currentAudioTracks = localStreamRef.current?.getAudioTracks() ?? [];
        const previousCameraTrack = cameraTrackBeforeShareRef.current;
        const canRestoreCamera = previousCameraTrack && previousCameraTrack.readyState === 'live';

        if (canRestoreCamera) {
          await replaceOutgoingVideoTrack(previousCameraTrack);
          const restored = new MediaStream([...currentAudioTracks, previousCameraTrack]);
          localStreamRef.current = restored;
          setLocalStream(restored);
          await applySenderQualityProfile(qualityProfileRef.current, { silent: true });
          setIsVideoOn(true);
        } else {
          await replaceOutgoingVideoTrack(null);
          const audioOnly = new MediaStream([...currentAudioTracks]);
          localStreamRef.current = audioOnly;
          setLocalStream(audioOnly);
          setIsVideoOn(false);
        }

        cameraTrackBeforeShareRef.current = null;
        setIsScreenSharing(false);
        toast({
          title: 'Screen sharing stopped',
          description: canRestoreCamera ? 'Switched back to camera' : 'Screen sharing has ended',
        });
      } else {
        // Start screen sharing
        const stream = await navigator.mediaDevices.getDisplayMedia({
          video: {
            displaySurface: 'monitor'
          } as MediaTrackConstraints,
          audio: false
        });

        setScreenStream(stream);

        const screenTrack = stream.getVideoTracks()[0];
        if (!screenTrack) {
          throw new Error('No screen video track available');
        }

        cameraTrackBeforeShareRef.current = localStreamRef.current?.getVideoTracks()[0] ?? null;

        const currentAudioTracks = localStreamRef.current?.getAudioTracks() ?? [];
        const merged = new MediaStream([...currentAudioTracks, screenTrack]);
        localStreamRef.current = merged;
        setLocalStream(merged);
        await replaceOutgoingVideoTrack(screenTrack);
        await applySenderQualityProfile(qualityProfileRef.current, { silent: true });

        // Stop screen sharing when user clicks "Stop Sharing" in browser
        screenTrack.onended = () => {
          void toggleScreenShare();
        };

        setIsScreenSharing(true);
        setIsVideoOn(true);
        toast({
          title: 'Screen sharing started',
          description: 'You are now sharing your screen',
        });
      }
    } catch (error: any) {
      console.error('Error toggling screen share:', error);
      toast({
        title: 'Screen sharing failed',
        description: error.message || 'Could not start screen sharing',
        variant: 'destructive'
      });
    }
  };

  const minimizeCall = () => {
    setIsCallMinimized(true);
  };

  const maximizeCall = () => {
    setIsCallMinimized(false);
  };

  // Cleanup on unmount ONLY (not when streams change)
  useEffect(() => {
    return () => {
      // Stop all sounds
      callSounds.stopAll();
      
      // Stop all streams (use refs to avoid stale closures)
      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach(track => track.stop());
      }
      if (remoteStreamRef.current) {
        remoteStreamRef.current.getTracks().forEach(track => track.stop());
      }
      if (screenStreamRef.current) {
        screenStreamRef.current.getTracks().forEach(track => track.stop());
      }

      if (callTimeoutId.current) {
        window.clearTimeout(callTimeoutId.current);
      }
      if (disconnectTimeoutId.current) {
        window.clearTimeout(disconnectTimeoutId.current);
      }
      incomingAutoMissTimeouts.current.forEach((timeoutId) => window.clearTimeout(timeoutId));
      incomingAutoMissTimeouts.current.clear();
      
      // Close peer connection
      if (peerConnection.current) {
        peerConnection.current.close();
      }
      
      // Unsubscribe from listeners
      if (callUnsubscribe.current) {
        callUnsubscribe.current();
      }
      if (incomingCallsUnsubscribe.current) {
        incomingCallsUnsubscribe.current();
      }
    };
     
  }, []); // Empty deps - only run on mount/unmount

  const value: CallContextType = {
    currentCall,
    incomingCall,
    isInCall,
    isMuted,
    isVideoOn,
    remoteAudioMuted,
    isScreenSharing,
    isAdaptiveQualityEnabled,
    adaptiveVideoMode,
    qualityProfile,
    isCallMinimized,
    canScreenShare,
    localStream,
    remoteStream,
    screenStream,
    callQuality,
    startCall,
    startScreenShareCall,
    endCall,
    acceptCall,
    rejectCall,
    toggleMute,
    toggleVideo,
    toggleRemoteAudio,
    toggleScreenShare,
    toggleAdaptiveQuality,
    setQualityProfile,
    minimizeCall,
    maximizeCall
  };

  return (
    <CallContext.Provider value={value}>
      {children}
      {/* Hidden video elements for WebRTC streams */}
      <video ref={localVideoRef} autoPlay playsInline muted style={{ display: 'none' }} />
      <video ref={remoteVideoRef} autoPlay playsInline style={{ display: 'none' }} />
    </CallContext.Provider>
  );
}
